import { useModal, useOverlay } from '@react-aria/overlays';
import { mergeProps, useLayoutEffect } from '@react-aria/utils';
import type { PlacementAxis, PopoverProps } from '@react-types/overlays';
import type { DOMRef } from '@react-types/shared';
import classNames from 'classnames';
import type { HTMLAttributes, ReactNode, RefObject } from 'react';
import React, { forwardRef, useRef, useState } from 'react';
import { useDOMRef } from '../../../utils';
import { Overlay } from '../controls/overlay';

interface PopoverWrapperProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  placement?: PlacementAxis;
  arrowProps?: HTMLAttributes<HTMLElement>;
  hideArrow?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  shouldCloseOnBlur?: boolean;
  isKeyboardDismissDisabled?: boolean;
  isNonModal?: boolean;
  isDismissable?: boolean;
}

let arrowPlacement = {
  left: 'right',
  right: 'right',
  top: 'bottom',
  bottom: 'bottom',
  center: 'center'
};

const Popover = (props: PopoverProps, ref: DOMRef<HTMLDivElement>) => {
  let {
    children,
    placement,
    arrowProps,
    onClose,
    shouldCloseOnBlur,
    hideArrow,
    isKeyboardDismissDisabled,
    isNonModal,
    isDismissable = true,
    ...otherProps
  } = props;
  let domRef = useDOMRef(ref);

  return (
    <Overlay {...otherProps}>
      <PopoverWrapper
        className={props.UNSAFE_className}
        style={props.UNSAFE_style}
        ref={domRef}
        placement={placement}
        arrowProps={arrowProps}
        onClose={onClose}
        shouldCloseOnBlur={shouldCloseOnBlur}
        isKeyboardDismissDisabled={isKeyboardDismissDisabled}
        hideArrow={hideArrow}
        isNonModal={isNonModal}
        isDismissable={isDismissable}
      >
        {children}
      </PopoverWrapper>
    </Overlay>
  );
};

const PopoverWrapper = forwardRef((props: PopoverWrapperProps, ref: RefObject<HTMLDivElement>) => {
  let {
    children,
    placement = 'bottom',
    arrowProps,
    isOpen,
    hideArrow,
    shouldCloseOnBlur,
    isKeyboardDismissDisabled,
    isNonModal,
    isDismissable,
    ...otherProps
  } = props;
  let { overlayProps } = useOverlay({ ...props, isDismissable: isDismissable && isOpen }, ref);
  let { modalProps } = useModal({
    isDisabled: isNonModal
  });

  return (
    <div
      {...mergeProps(otherProps, overlayProps, modalProps)}
      ref={ref}
      className={classNames(
        'popover-styles',
        `popover--${placement}-class`,
        {
          'popover--withTip-class': !hideArrow,
          'is-open-class': isOpen
        },
        classNames('overrideStyles', {}),
        otherProps.className as any
      )}
      role="presentation"
      data-testid="popover"
    >
      {children}
      {hideArrow ? null : <Arrow arrowProps={arrowProps} direction={arrowPlacement[placement]} />}
    </div>
  );
});

let ROOT_2 = Math.sqrt(2);

interface ArrowProps {
  direction: string;
  arrowProps?: object;
  style?: object;
}

const Arrow = (props: ArrowProps) => {
  let [size, setSize] = useState(20);
  let [borderWidth, setBorderWidth] = useState(1);
  let ref = useRef(null);

  useLayoutEffect(() => {
    if (ref.current) {
      let tipWidth = window.getComputedStyle(ref.current).getPropertyValue('popover-tip-size');
      if (tipWidth !== '') {
        setSize(parseInt(tipWidth, 10) / 2);
      }

      let orderWidth = window
        .getComputedStyle(ref.current)
        .getPropertyValue('popover-tip-borderWidth');
      if (orderWidth !== '') {
        setBorderWidth(parseInt(orderWidth, 10));
      }
    }
  }, [ref]);

  let landscape = props.direction === 'top' || props.direction === 'bottom';
  let mirror = props.direction === 'left' || props.direction === 'top';

  let borderDiagonal = borderWidth * ROOT_2;
  let halfBorderDiagonal = borderDiagonal / 2;

  let secondary = 2 * size + 2 * borderDiagonal;
  let primary = size + borderDiagonal;

  let primaryStart = mirror ? primary : 0;
  let primaryEnd = mirror ? halfBorderDiagonal : primary - halfBorderDiagonal;

  let secondaryStart = halfBorderDiagonal;
  let secondaryMiddle = secondary / 2;
  let secondaryEnd = secondary - halfBorderDiagonal;

  let pathData = landscape
    ? [
        'M',
        secondaryStart,
        primaryStart,
        'L',
        secondaryMiddle,
        primaryEnd,
        'L',
        secondaryEnd,
        primaryStart
      ]
    : [
        'M',
        primaryStart,
        secondaryStart,
        'L',
        primaryEnd,
        secondaryMiddle,
        'L',
        primaryStart,
        secondaryEnd
      ];
  let arrowProps = props.arrowProps;

  return (
    <svg
      xmlns="http://www.w3.org/svg/2000"
      width={Math.ceil(landscape ? secondary : primary)}
      height={Math.ceil(landscape ? primary : secondary)}
      style={props.style}
      className={classNames('popover-tip-styles', {})}
      ref={ref}
      {...arrowProps}
    >
      <path className={classNames('popover-tip-triangle-styles')} d={pathData.join(' ')} />
    </svg>
  );
};

let _Popover = forwardRef(Popover);
export { _Popover as Popover };
